import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { api } from '../api';
import { authHeader, getToken } from '../auth';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, 
  FileText, 
  Brain, 
  Download, 
  Copy, 
  CheckCircle,
  Loader,
  Compass,
  ArrowRightCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 2rem 1rem;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: #6b7280;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const UploadSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  border: 2px dashed ${props => props.isDragActive ? '#6366f1' : '#e5e7eb'};
  transition: all 0.3s;
`;

const Dropzone = styled.div`
  text-align: center;
  cursor: pointer;
  padding: 2rem;
  border-radius: 15px;
  background: ${props => props.isDragActive ? '#f0f9ff' : '#f9fafb'};
  transition: all 0.3s;
  
  &:hover {
    background: #f0f9ff;
  }
`;

const UploadIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: white;
`;

const UploadText = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const UploadSubtext = styled.div`
  color: #6b7280;
  margin-bottom: 1rem;
`;

const FileInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 10px;
  padding: 1rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FileIcon = styled.div`
  color: #0ea5e9;
`;

const FileDetails = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

const FileSize = styled.div`
  font-size: 0.9rem;
  color: #6b7280;
`;

const TextInputSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const PositionInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  margin-bottom: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const AnalyzeButton = styled.button`
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 auto;
  transition: all 0.3s;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ResultsSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  position: relative;
`;

const ResultsExport = styled.div`
  position: relative;
`;

const ResultCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ResultTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ResultContent = styled.div`
  color: #4b5563;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const ActionButton = styled.button`
  background: ${props => props.primary ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'white'};
  color: ${props => props.primary ? 'white' : '#6366f1'};
  border: 2px solid #6366f1;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(99, 102, 241, 0.3);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  color: #6366f1;
`;

const FutureBlock = styled.div`
  background: linear-gradient(135deg, #eef2ff 0%, #e0f2fe 100%);
  border: 1px solid #cbd5e1;
  border-radius: 18px;
  padding: 1.75rem;
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FutureText = styled.div`
  flex: 1;
  color: #0f172a;
`;

const FutureTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: #111827;
`;

const FutureDesc = styled.p`
  margin: 0;
  color: #475569;
  line-height: 1.5;
  font-size: 0.95rem;
`;

const FutureCta = styled.button`
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  padding: 0.85rem 1.2rem;
  border-radius: 999px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
  white-space: nowrap;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
    white-space: normal;
    text-align: center;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

const ModalCard = styled.div`
  background: white;
  border-radius: 18px;
  max-width: 720px;
  width: 95%;
  padding: 1.75rem;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.35);
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
  margin: 1rem 0 1.5rem;
`;

const ModalLabel = styled.label`
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.35rem;
  display: block;
`;

const ModalSelect = styled.select`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const SliderRow = styled.div`
  margin-top: 0.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const GhostButton = styled.button`
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #0f172a;
  padding: 0.75rem 1.1rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const SolidButton = styled.button`
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  padding: 0.75rem 1.2rem;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const ResultBadge = styled.span`
  display: inline-block;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-weight: 700;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
`;

const Watermark = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: 0.08;
  font-size: 3.5rem;
  font-weight: 800;
  color: #1f2937;
  transform: rotate(-12deg);
  text-transform: uppercase;
`;

const NoticeBanner = styled.div`
  background: #fef9c3;
  border: 1px solid #fde68a;
  color: #92400e;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const MiniResultCard = styled.div`
  background: #f8fafc;
  border: 1px dashed #cbd5f5;
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
`;

const MiniResultTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
`;

const MiniResultText = styled.p`
  color: #4b5563;
  margin: 0;
  line-height: 1.6;
`;

const MiniCta = styled.div`
  margin-top: 1.5rem;
  padding: 1.25rem;
  border-radius: 16px;
  background: linear-gradient(135deg, #eef2ff, #e0f2fe);
  border: 1px solid #cbd5f5;
  display: grid;
  gap: 0.75rem;
  text-align: center;
`;

const MiniCtaTitle = styled.p`
  margin: 0;
  font-weight: 700;
  color: #0f172a;
`;

const MiniCtaActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const MiniCtaPrimary = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.65rem 1.4rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  cursor: pointer;
`;

const MiniCtaSecondary = styled.button`
  border: 1px solid #cbd5f5;
  border-radius: 999px;
  padding: 0.65rem 1.4rem;
  font-weight: 600;
  background: #ffffff;
  color: #4338ca;
  cursor: pointer;
`;


const CVAnalyzer = () => {
  const navigate = useNavigate();
  const resultsRef = useRef(null);
  const [cvText, setCvText] = useState('');
  const [position, setPosition] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [miniPreview, setMiniPreview] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showFutureSim, setShowFutureSim] = useState(false);
  const [simAnswers, setSimAnswers] = useState({
    mainGoal: '',
    biggestStruggle: '',
    postponedThing: '',
    potentialScore: 50,
    careerMood: 'searching'
  });
  const [simLoading, setSimLoading] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [isWatermarking, setIsWatermarking] = useState(false);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      // Burada dosya okuma işlemi yapılacak
      toast.success('Dosya başarıyla yüklendi!');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1
  });

  const analyzeCV = async () => {
    if (!cvText.trim() && !uploadedFile) {
      toast.error('Lütfen CV metnini girin veya dosya yükleyin');
      return;
    }

    setIsAnalyzing(true);
    
    try {
      const token = getToken();
      const formData = new FormData();
      if (cvText.trim()) formData.append('cvText', cvText.trim());
      if (position.trim()) formData.append('position', position.trim());
      if (uploadedFile) formData.append('cvFile', uploadedFile);

      const endpoint = token ? '/api/cv/analyze' : '/api/cv/preview';
      const { data } = await api.post(endpoint, formData, {
        headers: token ? authHeader() : {}
      });

      if (data?.preview) {
        setMiniPreview(data);
        setResults(null);
        toast.success('Kısımlı sonuçlar hazır.');
      } else {
        setResults(data);
        setMiniPreview(null);
        toast.success('CV analizi tamamlandı!');
        if (data?.free_notice) {
          toast.success(data.free_notice);
        }
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Analiz sırasında bir hata oluştu';
      toast.error(msg);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Panoya kopyalandı!');
  };

  const downloadPdf = async () => {
    if (!results || !resultsRef.current) {
      toast.error('Önce analiz sonucunu oluşturmalısın.');
      return;
    }

    const element = resultsRef.current;
    const opt = {
      margin: 0,
      filename: 'cv-analiz-raporu.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    try {
      const { data } = await api.post('/api/pdf/claim', {}, { headers: authHeader() });
      const isPremium = !!data?.is_premium;

      if (!isPremium) {
        setIsWatermarking(true);
      }

      await html2pdf().from(element).set(opt).save();

      if (data?.free_notice) {
        toast.success(data.free_notice);
      }
    } catch (error) {
      const msg = error?.response?.data?.error || 'PDF oluşturulurken bir hata oluştu.';
      toast.error(msg);
    } finally {
      setIsWatermarking(false);
    }
  };

  const openFutureSimulator = () => {
    setShowFutureSim(true);
    setSimResult(null);
  };

  const runFutureSimulator = async () => {
    if (!simAnswers.mainGoal || !simAnswers.biggestStruggle || !simAnswers.postponedThing) {
      toast.error('Lütfen soruları tamamlayın');
      return;
    }
    setSimLoading(true);
    try {
      const payload = {
        answers: {
          ...simAnswers,
          potentialScore: Number(simAnswers.potentialScore)
        },
        cvSummary: {}
      };
      const { data } = await api.post('/api/career/future-simulator', payload, {
        headers: authHeader()
      });
      setSimResult(data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Kariyer gidişat analizi yapılamadı';
      toast.error(msg);
    } finally {
      setSimLoading(false);
    }
  };

  return (
    <PageContainer>
      <Container>
        <PageTitle>CV Analizi ve İyileştirme</PageTitle>
        <PageSubtitle>
          AI teknolojisi ile CV'nizi analiz edin, iyileştirin ve pozisyonunuza özel ön yazı hazırlayın
        </PageSubtitle>

        <UploadSection isDragActive={isDragActive}>
          <Dropzone {...getRootProps()} isDragActive={isDragActive}>
            <input {...getInputProps()} />
            <UploadIcon>
              <Upload size={32} />
            </UploadIcon>
            <UploadText>
              {isDragActive ? 'Dosyayı buraya bırakın' : 'CV dosyanızı yükleyin'}
            </UploadText>
            <UploadSubtext>
              Sadece PDF formatında dosya yükleyebilirsiniz
            </UploadSubtext>
          </Dropzone>
          
          {uploadedFile && (
            <FileInfo>
              <FileIcon>
                <FileText size={24} />
              </FileIcon>
              <FileDetails>
                <FileName>{uploadedFile.name}</FileName>
                <FileSize>{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</FileSize>
              </FileDetails>
            </FileInfo>
          )}
        </UploadSection>

        <TextInputSection>
          <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>Veya CV metnini doğrudan girin:</h3>
          <TextArea
            placeholder="CV metninizi buraya yapıştırın..."
            value={cvText}
            onChange={(e) => setCvText(e.target.value)}
          />
          
          <h3 style={{ marginBottom: '1rem', marginTop: '2rem', color: '#1f2937' }}>
            Başvuracağınız pozisyon (opsiyonel):
          </h3>
          <PositionInput
            placeholder="Örn: Yazılım Geliştirici, Pazarlama Uzmanı..."
            value={position}
            onChange={(e) => setPosition(e.target.value)}
          />
          
          <AnalyzeButton onClick={analyzeCV} disabled={isAnalyzing}>
            {isAnalyzing ? (
              <>
                <Loader className="animate-spin" size={20} />
                Analiz ediliyor...
              </>
            ) : (
              <>
                <Brain size={20} />
                CV'yi Analiz Et
              </>
            )}
          </AnalyzeButton>
        </TextInputSection>

        {isAnalyzing && (
          <ResultsSection>
            <LoadingSpinner>
              <Loader className="animate-spin" size={24} />
              <span>AI CV'nizi analiz ediyor...</span>
            </LoadingSpinner>
          </ResultsSection>
        )}

        {miniPreview && (
          <ResultsSection>
            <MiniResultCard>
                <MiniResultTitle>Mini Değer Özeti</MiniResultTitle>
              <MiniResultText>CV'nin ATS uyum skoru: {miniPreview.atsScore} / 100</MiniResultText>
              <MiniResultText>Güçlendirmek için {miniPreview.criticalSuggestionCount} kritik öneri var</MiniResultText>
            </MiniResultCard>
            <MiniCta>
              <MiniCtaTitle>Bu sorunları çözmek ister misin?</MiniCtaTitle>
              <MiniCtaActions>
                <MiniCtaPrimary type="button" onClick={() => navigate('/register')}>
                  Ücretsiz Üye Ol
                </MiniCtaPrimary>
                <MiniCtaSecondary type="button" onClick={() => navigate('/login')}>
                  Giriş Yap
                </MiniCtaSecondary>
              </MiniCtaActions>
            </MiniCta>
          </ResultsSection>
        )}

        {results && (
          <ResultsSection>
            {results.free_notice && (
              <NoticeBanner>{results.free_notice}</NoticeBanner>
            )}
            <ResultsExport ref={resultsRef}>
              {isWatermarking && <Watermark>ISHazir</Watermark>}
              <ResultCard>
                <ResultTitle>
                  <CheckCircle size={20} color="#10b981" />
                  Analiz Sonuçları
                </ResultTitle>
                <ResultContent>{`ATS uyumu dahil genel degerlendirme:\n\n${results.analysis}`}</ResultContent>
              </ResultCard>

              <ResultCard>
                <ResultTitle>
                  <FileText size={20} color="#6366f1" />
                  İyileştirilmiş CV
                </ResultTitle>
                <ResultContent>{results.improvedCV}</ResultContent>
              </ResultCard>

              <ResultCard>
                <ResultTitle>
                  <FileText size={20} color="#8b5cf6" />
                  Ön Yazı
                </ResultTitle>
                <ResultContent>{results.coverLetter}</ResultContent>
              </ResultCard>
            </ResultsExport>

            <ActionButtons>
              <ActionButton onClick={downloadPdf}>
                <Download size={16} />
                PDF İndir
              </ActionButton>
              <ActionButton onClick={() => copyToClipboard(results.improvedCV)}>
                <Copy size={16} />
                CV'yi Kopyalama
              </ActionButton>
              <ActionButton onClick={() => copyToClipboard(results.coverLetter)}>
                <Copy size={16} />
                Ön Yazıyı Kopyalama
              </ActionButton>
            </ActionButtons>
            <FutureBlock>
              <Compass size={28} color="#4f46e5" />
              <FutureText>
                <FutureTitle>Bu CV ile 5 Yil Sonra Nerede Olursun?</FutureTitle>
                <FutureDesc>
                  CV'n güçlü yönlerini ve eksiklerini gordun. Peki bu gidişat seni nasıl bir kariyere götürüyor? Bu bir tahmin değil, mevcut verilerle oluşturulmuş senaryodur.
                </FutureDesc>
              </FutureText>
              <FutureCta onClick={openFutureSimulator}>
                <ArrowRightCircle size={18} />
                5 Yıllık Kariyer Gidişatı Gör
              </FutureCta>
            </FutureBlock>
          </ResultsSection>
        )}
      </Container>

      {showFutureSim && (
        <ModalOverlay onClick={() => setShowFutureSim(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <FutureTitle style={{ marginBottom: '0.35rem' }}>
              5 Yıllık Kariyer Gidişat Analizi (Beta)
            </FutureTitle>
            <FutureDesc>
              Bu analiz; CV'n, hedeflerin ve karar alış biçimine göre önümüzdeki 5 yıla dair olası kariyer senaryolarını gösterir.
              Motivasyon konuşması değil; mevcut gidişatı netleştirir.
            </FutureDesc>

            <ModalGrid>
              <div>
                <ModalLabel>Şu anki ana hedefin</ModalLabel>
                <ModalSelect
                  value={simAnswers.mainGoal}
                  onChange={(e) => setSimAnswers({ ...simAnswers, mainGoal: e.target.value })}
                >
                  <option value="">Seçin</option>
                  <option value="career_money">Gelir artırmak</option>
                  <option value="better_role">Daha iyi pozisyon</option>
                  <option value="change_direction">Yön değiştirmek</option>
                  <option value="stability">Stabilite</option>
                  <option value="not_sure">Henüz net değil</option>
                </ModalSelect>
              </div>

              <div>
                <ModalLabel>Son 2 yılda en çok zorlayan</ModalLabel>
                <ModalSelect
                  value={simAnswers.biggestStruggle}
                  onChange={(e) => setSimAnswers({ ...simAnswers, biggestStruggle: e.target.value })}
                >
                  <option value="">Seçin</option>
                  <option value="indecision">Kararsızlık</option>
                  <option value="stuck_wrong_job">Yanlış işte kalmak</option>
                  <option value="procrastination">Erteleme</option>
                  <option value="money_pressure">Maddi baskı</option>
                  <option value="focus_issue">Odaklanamamak</option>
                </ModalSelect>
              </div>

              <div>
                <ModalLabel>En çok ertelediğin şey</ModalLabel>
                <ModalSelect
                  value={simAnswers.postponedThing}
                  onChange={(e) => setSimAnswers({ ...simAnswers, postponedThing: e.target.value })}
                >
                  <option value="">Seçin</option>
                  <option value="job_change">İş değiştirmek</option>
                  <option value="take_risk">Risk almak</option>
                  <option value="focus_one_project">Bir projeye odaklanmak</option>
                  <option value="self_improve">Kendimi geliştirmek</option>
                  <option value="net_decision">Net bir karar vermek</option>
                </ModalSelect>
              </div>

              <div>
                <ModalLabel>Potansiyelimin altında yaşıyorum (0-100)</ModalLabel>
                <SliderRow>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={simAnswers.potentialScore}
                    onChange={(e) => setSimAnswers({ ...simAnswers, potentialScore: Number(e.target.value) })}
                    style={{ width: '100%' }}
                  />
                  <div style={{ textAlign: 'right', color: '#475569', fontWeight: 600 }}>
                    %{simAnswers.potentialScore}
                  </div>
                </SliderRow>
              </div>

              <div>
                <ModalLabel>Kariyer ruh halin</ModalLabel>
                <ModalSelect
                  value={simAnswers.careerMood}
                  onChange={(e) => setSimAnswers({ ...simAnswers, careerMood: e.target.value })}
                >
                  <option value="searching">Arayışta</option>
                  <option value="drifting">Sürüklendiğimi hissediyorum</option>
                  <option value="stable">Stabil ama sorgulayan</option>
                </ModalSelect>
              </div>
            </ModalGrid>

            <ModalActions>
              <GhostButton onClick={() => setShowFutureSim(false)}>Kapat</GhostButton>
              <SolidButton onClick={runFutureSimulator} disabled={simLoading}>
                {simLoading ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Hesaplanıyor...
                  </>
                ) : (
                  <>
                    <Compass size={16} />
                    Analizi Başlat
                  </>
                )}
              </SolidButton>
            </ModalActions>

            {simResult && (
              <ResultCard style={{ marginTop: '1rem' }}>
                <ResultBadge>{simResult.profileLabel}</ResultBadge>
                <ResultTitle style={{ marginTop: '0.25rem' }}>
                  Kariyer Gidişatın
                </ResultTitle>
                <ResultContent style={{ marginBottom: '1rem' }}>
                  {simResult.currentTrajectory}
                </ResultContent>
                <ResultContent>
                  <strong>1 Yıl Sonra:</strong> {simResult.year1 || '-'}
                  {'\n\n'}
                  <strong>3 Yıl Sonra:</strong> {simResult.year3 || '-'}
                  {'\n\n'}
                  <strong>5 Yıl Sonra:</strong> {simResult.year5 || '-'}
                  {'\n\n'}
                  <strong>En Büyük Sessiz Risk:</strong> {simResult.silentRisk || '-'}
                  {'\n\n'}
                  <strong>Tekrarlanan Desen:</strong> {simResult.repeatingPattern || '-'}
                  {'\n\n'}
                  <strong>Tek Kritik Hamle:</strong> {simResult.leveragePoint || '-'}
                </ResultContent>
              </ResultCard>
            )}
          </ModalCard>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default CVAnalyzer;




